import { useEffect, useState } from 'react'
import type { Diamond } from '../api/types'
import { favoritesApi, favoriteStonesApi } from '../api/endpoints'
import {
  loadFavoriteSuppliers,
  isFavoriteSupplier,
  setFavoriteSupplier,
  subscribeFavorites,
  loadFavoriteStones,
  isFavoriteStone,
  setFavoriteStone,
  subscribeFavoriteStones,
} from '../lib/sessionCache'

/**
 * FavoriteButtons — the two DISTINCT favourite toggles, extracted from
 * MediaModal so any stone surface (the stone modal, the chat result card)
 * can render the exact same control instead of re-implementing the gate.
 *
 * Two favourites in this product, and they must never read as the same action
 * (StoneActions.tsx says so where they sit side by side):
 *   · favourite SUPPLIER — a HEART, saves a contact  (/me/favorite-suppliers)
 *   · save STONE         — a BOOKMARK, saves a stone (/me/favorite-stones)
 *
 * Both read/write the shared `lib/sessionCache` favourite helpers — one list
 * fetch per session, optimistic-then-corrected writes, and a subscribe so a
 * toggle from anywhere repaints every mounted button at once.
 */

/**
 * Heart toggle for the listing supplier — saves the supplier (with a contact
 * snapshot) to the buyer's favorites (/favorites). Signed-in only: the
 * supplier block only exists for a verified caller, so `supplierId == null`
 * (anon / unverified) renders nothing. State loads lazily; failures degrade to
 * unhearted.
 */
export function FavoriteSupplierButton({ stone: s }: { stone: Diamond }) {
  const supplierId = s.supplierId ?? null
  // Read from the shared session cache — one favorites fetch all session,
  // not one per modal open / prev-next hop. `fav` is the synchronous cache
  // read; null until the (single) fetch lands.
  const [fav, setFav] = useState<boolean | null>(() =>
    supplierId == null ? false : isFavoriteSupplier(supplierId))
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (supplierId == null) return
    // Subscribe first so a toggle (or the initial load) from anywhere repaints
    // this heart, then ensure the set is loaded once.
    const sync = () => setFav(isFavoriteSupplier(supplierId) ?? false)
    const unsub = subscribeFavorites(sync)
    void loadFavoriteSuppliers().then(sync)
    return unsub
  }, [supplierId])

  if (supplierId == null) return null

  async function toggle() {
    if (busy || supplierId == null) return
    setBusy(true)
    const next = !(fav ?? false)
    setFavoriteSupplier(supplierId, next) // optimistic — updates cache + all hearts
    try {
      const r = await favoritesApi.toggle({
        supplierId,
        name: s.supplier?.name ?? null,
        contactName: s.supplier?.contactName ?? null,
        phone: s.supplier?.phone ?? null,
        email: s.supplier?.email ?? null,
        country: s.origin ?? null,
      })
      setFavoriteSupplier(supplierId, r.favorited)
    } catch {
      setFavoriteSupplier(supplierId, !next) // revert
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={busy || fav === null}
      aria-pressed={fav === true}
      aria-label={fav ? 'Remove supplier from favorites' : 'Save supplier to favorites'}
      title={fav ? 'In your favorites' : 'Save to favorites'}
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
        fav ? 'text-gold' : 'text-soft hover:text-gold'
      } disabled:opacity-40`}
      data-testid="favorite-supplier"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill={fav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
      </svg>
    </button>
  )
}

/**
 * "Save stone" BOOKMARK toggle — saves this diamond (by stockId only) to the
 * buyer's /favorites "Saved stones" list. Distinct from the supplier HEART
 * (which saves a contact): a bookmark shape + a "Save"/"Saved" pill so the two
 * controls never read as the same action. Signed-in only — anon toggles 401
 * and revert. State loads lazily from the shared session cache; failures
 * degrade to unsaved.
 */
export function FavoriteStoneButton({ stone: s }: { stone: Diamond }) {
  const stockId = s.stockId
  const [fav, setFav] = useState<boolean | null>(() => isFavoriteStone(stockId))
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const sync = () => setFav(isFavoriteStone(stockId) ?? false)
    const unsub = subscribeFavoriteStones(sync)
    void loadFavoriteStones().then(sync)
    return unsub
  }, [stockId])

  async function toggle() {
    if (busy) return
    setBusy(true)
    const next = !(fav ?? false)
    setFavoriteStone(stockId, next) // optimistic — updates cache + all bookmarks
    try {
      const r = await favoriteStonesApi.toggle(stockId)
      setFavoriteStone(stockId, r.favorited)
    } catch {
      setFavoriteStone(stockId, !next) // revert
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={() => void toggle()}
      disabled={busy || fav === null}
      aria-pressed={fav === true}
      aria-label={fav ? 'Remove stone from saved' : 'Save stone'}
      title={fav ? 'Saved to your favorites' : 'Save this stone'}
      className={`inline-flex h-7 shrink-0 items-center gap-1 rounded-full border px-2.5 text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40 ${
        fav
          ? 'border-gold bg-gold/10 text-gold'
          : 'border-line text-navy-500 hover:border-gold/50 hover:text-gold'
      }`}
      data-testid="favorite-stone"
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill={fav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
      </svg>
      {fav ? 'Saved' : 'Save'}
    </button>
  )
}
